import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Module-level cache so navigating between pages doesn't re-fetch the
 * same content. Populated by useContent calls + the admin editor's
 * post-save invalidate.
 */
const cache = new Map<string, unknown>();
const subscribers = new Map<string, Set<(v: unknown) => void>>();

function notify(key: string, value: unknown) {
  cache.set(key, value);
  subscribers.get(key)?.forEach((fn) => fn(value));
}

function subscribe(key: string, fn: (v: unknown) => void): () => void {
  if (!subscribers.has(key)) subscribers.set(key, new Set());
  subscribers.get(key)!.add(fn);
  return () => subscribers.get(key)?.delete(fn);
}

/** Keys asked for in the same moment go in one request, and nobody asks for a key already on its way. */
const inFlight = new Map<string, Promise<unknown>>();
let queued: Map<string, (value: unknown) => void> | null = null;
let queueFailed: ((reason: unknown) => void)[] = [];

function sendQueue() {
  const waiting = queued!;
  const failed = queueFailed;
  queued = null;
  queueFailed = [];
  supabase!
    .from('site_content')
    .select('key, value')
    .in('key', [...waiting.keys()])
    .then(({ data, error }) => {
      waiting.forEach((_, key) => inFlight.delete(key));
      if (error) {
        failed.forEach((reject) => reject(error));
        return;
      }
      const rows = new Map((data ?? []).map((row) => [row.key as string, row.value as unknown]));
      waiting.forEach((resolve, key) => resolve(rows.get(key)));
    }, (reason) => {
      waiting.forEach((_, key) => inFlight.delete(key));
      failed.forEach((reject) => reject(reason));
    });
}

function readRow(key: string): Promise<unknown> {
  const existing = inFlight.get(key);
  if (existing) return existing;
  const request = new Promise<unknown>((resolve, reject) => {
    if (!queued) {
      queued = new Map();
      setTimeout(sendQueue, 0);
    }
    queued.set(key, resolve);
    queueFailed.push(reject);
  });
  inFlight.set(key, request);
  return request;
}

/** Settles once every content request made so far has come back, whether or not it worked. */
export function contentSettled(): Promise<void> {
  return Promise.allSettled([...inFlight.values()]).then(() => undefined);
}

/**
 * Fetch a single key from Supabase and broadcast it to all subscribers.
 * Internal helper — used both for initial loads and post-save refresh.
 */
async function fetchAndBroadcast<T>(key: string, fallback: T): Promise<T | null> {
  if (!supabase) return null;
  const value = await readRow(key);
  const next = value !== undefined && value !== null ? (value as T) : fallback;
  notify(key, next);
  return next;
}

/**
 * Bust the cache for a single key — call after a successful admin save
 * to immediately re-pull from Supabase and push the new value to every
 * mounted consumer of that key.
 */
export function invalidateContent(key: string): void {
  cache.delete(key);
  // A read that started before the save could bring back the old value.
  inFlight.delete(key);
  // Refetch immediately so all subscribers receive the fresh value
  // instead of momentarily falling back to the seed default.
  if (supabase) {
    supabase
      .from('site_content')
      .select('value')
      .eq('key', key)
      .maybeSingle()
      .then(({ data: row }) => {
        // If there's a row, broadcast its value; if not, broadcast undefined
        // so subscribers refetch (their fallback handler runs).
        notify(key, row?.value ?? undefined);
      });
  } else {
    notify(key, undefined);
  }
}

/**
 * Fetch a JSON content row from Supabase `site_content` keyed by `key`.
 * Falls back to `fallback` (seed defaults baked into the component) if
 * no row exists, Supabase is unreachable, or the row is malformed.
 *
 * Public anon read — no auth needed; safe in marketing components.
 */
export function useContent<T>(key: string, fallback: T): {
  data: T;
  loading: boolean;
  error: Error | null;
} {
  const cached = cache.get(key) as T | undefined;
  const [data, setData] = useState<T>(cached ?? fallback);
  const [loading, setLoading] = useState(cached === undefined);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchRow = async () => {
      try {
        const next = await fetchAndBroadcast<T>(key, fallback);
        if (cancelled) return;
        if (next !== null) setData(next);
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError(err as Error);
          setLoading(false);
        }
      }
    };

    if (cached === undefined) {
      fetchRow();
    } else {
      setLoading(false);
    }

    const unsub = subscribe(key, (v) => {
      if (cancelled) return;
      if (v === undefined) {
        // Cache invalidated with no fresh value yet — refetch from Supabase
        // so this consumer doesn't render the seed fallback.
        fetchRow();
      } else {
        setData(v as T);
      }
    });

    return () => {
      cancelled = true;
      unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { data, loading, error };
}
